import { retryAfterUnauthorized } from "./session";
import { API_BASE_URL } from "../config";

/**
 * Workflow Streaming API Service
 *
 * Handles Server-Sent Events (SSE) connections for real-time workflow execution streaming.
 * Provides methods to execute workflow stages with streaming updates.
 */

/**
 * Get authentication headers
 * @returns {object} Headers object with auth token
 */
const getAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  // Get auth token from localStorage or sessionStorage
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
  } catch (_) {
    user = {};
  }

  if (user.token) {
    headers["Authorization"] = `Bearer ${user.token}`;
  }

  return headers;
};

// A live stream is never silent this long: the server pings every 15s and long finalize passes
// emit a heartbeat every 20s. A gap this large means the socket went half-open — dropped by an
// intermediary without an RST — in which case reader.read() never resolves and never rejects, so
// the run would hang forever with no callback to recover it.
export const STREAM_STALL_MS = 90000;

/**
 * Internal helper: Stream SSE request with shared logic
 * @private
 * @param {string} url - Full URL for SSE endpoint
 * @param {object} payload - Request body
 * @param {object} options - Streaming options
 * @returns {object} Controller with abort() method
 */
function _streamSSERequest(url, payload, options = {}) {
  const {
    onEvent,
    onError,
    onComplete,
    onConnectionOpen,
    onStall,
    signal,
    stallMs = STREAM_STALL_MS,
  } = options;

  const controller = new AbortController();
  const fetchSignal = signal || controller.signal;

  // Start streaming request
  (async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
        signal: fetchSignal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP error! status: ${response.status}`,
        }));

        // Handle 401 Unauthorized - Token expired
        if (response.status === 401) {
          // the access token may simply have expired: renew it, then the caller retries

          await retryAfterUnauthorized(() => undefined);
          throw new Error(errorData.detail || "Session expired. Please login again.");
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Connection opened successfully
      if (onConnectionOpen) {
        onConnectionOpen();
      }

      // Read the response stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        // Stop processing immediately if the signal has been aborted
        if (fetchSignal.aborted) {
          break;
        }

        // Race the read against a stall timeout so a half-open socket cannot hang the run.
        let stallTimer = null;
        const stalled = Symbol("stalled");
        const result = await Promise.race([
          reader.read(),
          new Promise((resolve) => {
            stallTimer = setTimeout(() => resolve(stalled), stallMs);
          }),
        ]);
        if (stallTimer) {
          clearTimeout(stallTimer);
        }

        if (result === stalled) {
          console.warn(`Stream stalled: no data for ${stallMs / 1000}s`);
          try {
            await reader.cancel();
          } catch {
            // Cancelling a dead reader can itself throw; the stall handling is what matters.
          }
          if (onStall) {
            onStall();
          } else if (onComplete) {
            onComplete();
          }
          break;
        }

        const { done, value } = result;

        if (done) {
          // Stream complete
          if (onComplete) {
            onComplete();
          }
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (end with double newline)
        const lines = buffer.split("\n\n");

        // Keep the last incomplete message in buffer
        buffer = lines.pop() || "";

        // Process each complete message
        for (const line of lines) {
          if (fetchSignal.aborted) {
            break;
          }

          if (line.trim()) {
            // Parse SSE format: "data: {json}"
            const dataMatch = line.match(/^data:\s*(.+)$/m);

            if (dataMatch && dataMatch[1]) {
              try {
                const eventData = JSON.parse(dataMatch[1]);

                // Server keepalive so proxies don't cut idle streams
                if (eventData.type === "ping") {
                  continue;
                }

                // Call event handler
                if (onEvent) {
                  onEvent(eventData);
                }

                // Check if execution is complete
                if (eventData.event_type === "execution_complete") {
                  if (onComplete) {
                    onComplete();
                  }
                }
              } catch (parseError) {
                if (onError) {
                  onError(new Error("Failed to parse event data"));
                }
              }
            }
          }
        }
      }
    } catch (error) {
      // Only report non-abort errors
      if (error.name !== "AbortError") {
        if (onError) {
          onError(error);
        }
      }
    }
  })();

  // Return controller for cancellation
  return {
    abort: () => controller.abort(),
    signal: controller.signal,
  };
}

/**
 * Execute a workflow stage with streaming updates
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number to execute
 * @param {object} options - Execution options
 * @param {string} options.userQuery - User query for the stage
 * @param {boolean} options.enableWebValidation - Enable web validation (default: false)
 * @param {string} [options.conversationId] - Optional conversation ID for Codifier chat
 * @param {function} options.onEvent - Callback for each SSE event (event) => void
 * @param {function} options.onError - Callback for errors (error) => void
 * @param {function} options.onComplete - Callback when execution completes () => void
 * @param {function} options.onConnectionOpen - Callback when connection opens () => void
 * @param {function} options.onStall - Callback when the socket goes silent () => void
 * @param {AbortSignal} options.signal - AbortSignal to cancel the request
 * @returns {object} Controller with abort() method
 */
export function executeStageStream(workflowId, stageNumber, options = {}) {
  const { userQuery, enableWebValidation = false, reportType = "comprehensive", onError } = options;

  // Validate required parameters
  if (!workflowId || typeof workflowId !== "string") {
    const error = new Error("Invalid workflow ID");
    if (onError) {
      onError(error);
    }
    throw error;
  }

  if (!stageNumber || typeof stageNumber !== "number") {
    const error = new Error("Invalid stage number");
    if (onError) {
      onError(error);
    }
    throw error;
  }

  if (!userQuery || typeof userQuery !== "string" || !userQuery.trim()) {
    const error = new Error("User query is required");
    if (onError) {
      onError(error);
    }
    throw error;
  }

  // Build request payload
  const payload = {
    user_query: userQuery,
    enable_web_validation: enableWebValidation,
    conversation_id: options.conversationId || null,
    report_type: reportType,
  };

  // Create URL for SSE endpoint
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/execute-stream`;

  // Delegate to shared SSE streaming helper
  return _streamSSERequest(url, payload, options);
}

/**
 * Execute workflow stage with simplified promise-based interface
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number
 * @param {string} userQuery - User query
 * @param {boolean} enableWebValidation - Enable web validation
 * @returns {Promise} Promise that resolves with array of all events
 */
export function executeStageStreamPromise(
  workflowId,
  stageNumber,
  userQuery,
  enableWebValidation = false
) {
  return new Promise((resolve, reject) => {
    const events = [];

    executeStageStream(workflowId, stageNumber, {
      userQuery,
      enableWebValidation,
      onEvent: (event) => {
        events.push(event);
      },
      onError: (error) => {
        reject(error);
      },
      onComplete: () => {
        resolve(events);
      },
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// SEQUENTIAL STAGE EXECUTION
// Each function streams one discrete step and returns an abort controller.
// ─────────────────────────────────────────────────────────────────────────────

export function generateStageSummary(workflowId, stageNumber, userQuery, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/generate-summary`;
  const body = { user_query: userQuery };
  if (options.projectId) {
    body.project_id = options.projectId;
  }
  // Which of the 4 agents to run; captured here and reused by the answer + full report.
  if (options.selectedAgents && options.selectedAgents.length) {
    body.selected_agents = options.selectedAgents;
  }
  return _streamSSERequest(url, body, options);
}

/**
 * Answer a pending human-in-the-loop gate and continue the run.
 *
 * Resuming is a NEW stream, not a reconnection: the paused run's stream already ended
 * (the backend persisted the gate and closed it), so a closed tab or a backend restart
 * costs nothing. Everything needed to continue was stored with the gate.
 *
 * @param {string} workflowId
 * @param {number} stageNumber
 * @param {string} gateId - from the gate_request event
 * @param {{action?: "approve"|"reject", outline?: object[], note?: string}} answer
 */
/**
 * The gate a stage is parked at, or null.
 *
 * A gate is persisted so the pause survives a closed tab or a backend restart — but the
 * panel only learns about one from a live `gate_request` event, so a page reload would
 * otherwise lose the card while the run stays parked. Called on mount to recover it.
 */
export async function fetchPendingGate(workflowId, stageNumber) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/gates/pending`;
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  return data?.gate || null;
}

export function respondToGate(workflowId, stageNumber, gateId, answer = {}, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/gates/${gateId}/respond`;
  const body = { action: answer.action || "approve" };
  if (answer.outline) {
    body.outline = answer.outline;
  }
  // {} is a real answer to the evidence gate ("continue without hints") and must reach
  // the backend — omitting it would leave the gate unanswered and it would re-fire.
  if (answer.evidence_hints !== undefined) {
    body.evidence_hints = answer.evidence_hints;
  }
  if (answer.note) {
    body.note = answer.note;
  }
  return _streamSSERequest(url, body, options);
}

export function generateStageAnswer(workflowId, stageNumber, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/generate-answer`;
  const body = {};
  if (options.projectId) {
    body.project_id = options.projectId;
  }
  // This run's agent selection overrides the one captured at the summary step.
  if (options.selectedAgents && options.selectedAgents.length) {
    body.selected_agents = options.selectedAgents;
  }
  return _streamSSERequest(url, body, options);
}

// Stop the stage's server-side answer run; closing the stream alone leaves it running.
export async function cancelStageAnswer(workflowId, stageNumber) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/cancel-answer`;
  const response = await fetch(url, { method: "POST", headers: getAuthHeaders() });
  return response.ok;
}

export function generateParentTasks(workflowId, stageNumber, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/generate-parent-tasks`;
  const body = {};
  if (options.projectId) {
    body.project_id = options.projectId;
  }
  return _streamSSERequest(url, body, options);
}

export function generateFullReport(workflowId, stageNumber, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/generate-full-report`;
  const body = {};
  if (options.projectId) {
    body.project_id = options.projectId;
  }
  // This run's agent selection overrides the one captured at the summary step.
  if (options.selectedAgents && options.selectedAgents.length) {
    body.selected_agents = options.selectedAgents;
  }
  return _streamSSERequest(url, body, options);
}

export function generateChildTasks(workflowId, stageNumber, parentTaskId, options = {}) {
  const url = `${API_BASE_URL}/api/faa/workflows/${workflowId}/stages/${stageNumber}/generate-child-tasks`;
  return _streamSSERequest(url, { parent_task_id: parentTaskId }, options);
}

const workflowStreamApi = {
  executeStageStream,
  executeStageStreamPromise,
  generateStageSummary,
  generateStageAnswer,
  cancelStageAnswer,
  respondToGate,
  fetchPendingGate,
  generateParentTasks,
  generateFullReport,
  generateChildTasks,
};

export default workflowStreamApi;
